<template>
  <div class="modal-backdrop">
    <div class="popup-content energy-modal ">
      <button class="energy-modal__close" type="button" @click="$emit('close')">
        ✕
      </button>

      <h2 class="energy-modal__title">Индикатор энергии</h2>

      <div class="energy-modal__content">
        <p class="energy-modal__intro">
          Выполнение задач требует энергии.
          <strong>Навигатор Эмоций</strong>
          помогает эффективнее выполнять задачи и меньше расходовать энергию.
          Для наглядности используется индикатор в виде батарейки.
          В зависимости от соотношения задач и эмоций меняется уровень заряда:
        </p>

        <div
            v-for="item in batteryRules"
            :key="item.id"
            class="energy-rule"
        >
          <BatteryIndicator
              :level="item.level"
              :show-label="item.showLabel"
              :label="item.label"
          />

          <p class="energy-rule__text" v-html="item.text"></p>
        </div>

        <div class="energy-modal__footer">
          <div class="energy-modal__why">Почему так?</div>
          <p class="energy-modal__note">
            *Изменение эмоции требует больше сил, потому что вы:
          </p>
          <ol class="energy-modal__list">
            <li>Дополнительно тратите силы на переключение эмоции.</li>
            <li>Тратите энергию на выполнение текущей задачи.</li>
          </ol>
        </div>
      </div>

      <div class="battery-update-settings">
        <custom-select
            :labelWidth="40"
            name="rowsCount"
            v-model="batteryResetInterval"
            label="Период пополнения энергии"
            :options="batteryResetSettings"
            @update:modelValue="updateResetInterval"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BatteryIndicator from './BatteryIndicator.vue'
import {computed} from "@vue/reactivity";
import {useStore} from "vuex";
import CustomSelect from "@/components/Forms/Fields/CustomSelect.vue";
import {onBeforeMount, onBeforeUnmount, ref, watch} from "@vue/runtime-core";
import {notify} from "@kyvg/vue3-notification";

export default {
  name: 'BatteryInfoModal',
  components: {
    CustomSelect,
    BatteryIndicator,
  },
  emits: ['close'],

  setup() {
    const batteryRules = [
      {
        id: 1,
        level: 100,
        showLabel: true,
        label: '+3%',
        text: 'Когда эмоция и задача <strong>соответствуют</strong>, прибавляется 3% энергии.',
      },
      {
        id: 2,
        level: 70,
        showLabel: true,
        label: '-3%',
        text: 'Когда эмоция и задача <strong>не соответствуют</strong>, и вы <strong>меняете задачу</strong> под актуальную эмоцию, то тратится 3% энергии.',
      },
      {
        id: 3,
        level: 45,
        showLabel: true,
        label: '-5%',
        text: 'Когда эмоция и задача <strong>не соответствуют</strong>, и вы <strong>меняете эмоцию</strong> под актуальную задачу, то тратится 5%* энергии.',
      },
    ];

    const batteryResetSettings = [
      {
        id: 'daily',
        text: 'Каждый день'
      },
      {
        id: 'weekly',
        text: 'Каждую неделю'
      },
      {
        id: 'monthly',
        text: 'Каждый месяц'
      },
    ];

    const store = useStore();
    const currentSettings = computed(() => store.state.tracker.settingsData);
    const batteryResetInterval = ref('weekly');

    const updateResetInterval = () => {
      store.dispatch('tracker/setCurrentSettings', {
        mode: currentSettings.value.current_mode,
        battery_reset_interval: batteryResetInterval.value,
      }).then(() => {
        notify({
          type: "success",
          title: 'Период обновлен',
        });
        store.dispatch('tracker/getCurrentSettings');
      });
    }

    onBeforeMount(() => {
      batteryResetInterval.value = currentSettings.value?.battery_reset_interval ?? 'weekly';
    });

    watch(() => currentSettings.value, () => {
      batteryResetInterval.value = currentSettings.value?.battery_reset_interval ?? 'weekly';
    }, {immediate: true});

    return {
      batteryRules,
      currentSettings,
      batteryResetInterval,
      batteryResetSettings,
      updateResetInterval
    }
  },
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.energy-modal {
  position: relative;
  width: 720px;
  max-width: calc(100vw - 32px);
  background: #f3f3f3;
  border-radius: 26px;
  padding: 26px 34px 28px;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
  color: #2c2c2c;

  .form-group {
    font-size: 16px;
    font-weight: 600;
  }
}

.energy-modal__close {
  position: absolute;
  top: 18px;
  right: 18px;
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: #8f8f8f;
  cursor: pointer;
  padding: 0;
}

.energy-modal__title {
  margin: 0 0 18px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #384559;
}

.energy-modal__content {
  font-size: 18px;
  line-height: 1.22;
}

.energy-modal__intro {
  margin: 0 0 18px;
}

.energy-rule {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 18px;
}

.energy-rule__text {
  margin: 0;
  flex: 1;
}

.energy-modal__footer {
  margin-top: 12px;
  font-size: 14px;
}

.energy-modal__why {
  font-size: 18px;
  font-style: italic;
  font-weight: 700;
  margin-bottom: 4px;
}

.energy-modal__note {
  margin: 0 0 4px;
  font-style: italic;
}

.energy-modal__list {
  margin: 0;
  padding-left: 22px;
  font-style: italic;
}

.battery-update-settings {
  margin-top: 25px;
}
</style>